// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2016
//
// ## ChordsAndInversionID.js
// Identification of chords by letter name, quality, inversion &/or figures
//
/*
 * @param displayDiv
 * @param options = {
      drill: bool, default false
      skills: [skillId, ...] // for drill mode
      questions: ["F#3 Dom 42"]
      inversion: "numbers", "figures", false // should user ID inversion?
      quality: bool // should user ID quality?
      root: bool // should user click/tap the root?
      chords: {
        type: "triads", "sevenths", "both" // defaults to inspecting skills
          // in drill mode
        spacing: "open"/"close" // defaults to close unless triad_spacing is
          // present in skills list
      }
   }
 *
 */
/**
 * Question Formats:
 *  - chord/clef: Gb Maj 63/treble
 *  - [NoteHolder]/clef: [Bb3-Db4-Gb4]/treble
 */

import { clefNames } from 'shared/definitions/musicTheory/clefs';
import * as Skills from '../uTheory.Skills/skills';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import utils from '../utils';
import KeySignature from '../uTheory.Notation/keySignature';
import Music from '../uTheory.Notation/music';
import Clef from '../uTheory.Notation/clef';
import Staff from '../uTheory.Notation/staff';
import NoteHolder from '../uTheory.Notation/noteHolder';
import Rhythm from '../uTheory.Notation/rhythm';
import Editable from '../uTheory.Notation/editable';
import Measure from '../uTheory.Notation/measure';
import Voice from '../uTheory.Notation/voice';
import Chord from '../uTheory.Notation/chord';
import type { AnswerAndData } from './interactiveQuestionPrototype';
import type { ChordIdAnswer } from './chordIdButtons';

interface ChordAndInversionIDQuestion {
  chord: Chord;
  text: number;
  skills: string[];
  clef: string;
  noteHolder: NoteHolder;
}
interface ChordAndInversionIDInteractiveOptions extends InteractiveOptions {
  /** Should the user id the inversion? If so, how? */
  inversion: 'numbers' | 'figures' | false;
  /** Should the user ID the quality? */
  quality: boolean;
  /** Should the user ID the root? */
  root: boolean;
  /**
   * What type of chords will we show? In drill mode it can inspect the
   * skills to figure this out. In checkpoint & manual question mode,
   * this must be specified
   */
  chords?: {
    /** Defaults to inspecting skills in drill mode */
    type?: 'triads' | 'sevenths' | 'both';
    /**
     * Defaults to 'close' unless triad_spacing or seventh_chord_spacing
     * are present in the skills list.
     */
    spacing?: 'open' | 'close';
  };
  /** Display chord on a grand staff? Defaults to false unless we're using open spacings */
  grandStaff?: boolean;
}

const seventhSkills = [
  'major_seventh_chords',
  'minor_seventh_chords',
  'dominant_seventh_chords',
  'diminished_seventh_chords',
  'half_diminished_seventh_chords',
];
const triadSkills = ['major_triads', 'minor_triads', 'augmented_triads', 'diminished_triads'];

const spacingSkills = ['triad_spacing', 'seventh_chord_spacing'];

const BASS = 1;
const TREBLE = 0;

export default class ChordAndInversionID extends InteractivePrototype {
  declare options: ChordAndInversionIDInteractiveOptions;

  staves: {
    staff: Staff;
    voice: Voice;
    noteHolder: NoteHolder;
    clef: Clef;
  }[];

  qg: QuestionGenerator;

  // Public constructor
  constructor(displayDiv: HTMLDivElement, options: ChordAndInversionIDInteractiveOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = [
      'major_triads',
      'minor_triads',
      'augmented_triads',
      'diminished_triads',
      'triad_spacing',
      'triad_inversion',
      'triad_figured_bass',
      'major_seventh_chords',
      'minor_seventh_chords',
      'dominant_seventh_chords',
      'diminished_seventh_chords',
      'half_diminished_seventh_chords',
      'seventh_chord_spacing',
      'seventh_chord_inversion',
      'seventh_chord_figured_bass',
    ];
    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: ChordAndInversionIDInteractiveOptions): Promise<any> {
    // this.debug(utils.clone(options, true));
    // Set defaults on options object:
    options.skills = options.skills || [];
    options.skills = options.skills.map(Skills.makeString);

    // Do we have a valid skills set?
    const triadSpacingOrInversion =
      options.skills.indexOf('triad_figured_bass') !== -1 ||
      options.skills.indexOf('triad_inversion') !== -1 ||
      options.skills.indexOf('triad_spacing') !== -1;
    const noTriadIntersection = !utils.intersects(triadSkills, options.skills);

    // If we're doing triad spacing or inversion, but there's no
    // triad skill, put all the triads in.
    if (triadSpacingOrInversion && noTriadIntersection) {
      // this.debug('adding triads');
      options.skills = options.skills.concat(triadSkills);
    }

    this.debug(
      `triadSpacingOrInversion: ${triadSpacingOrInversion}\n noTriadIntersection: ${noTriadIntersection}`
    );

    const seventhSpacingOrInversion =
      options.skills.indexOf('seventh_chord_figured_bass') !== -1 ||
      options.skills.indexOf('seventh_chord_inversion') !== -1 ||
      options.skills.indexOf('seventh_chord_spacing') !== -1;
    const noSeventhIntersection = !utils.intersects(seventhSkills, options.skills);

    this.debug(
      `seventhSpacingOrInversion: ${seventhSpacingOrInversion}\n noSeventhIntersection: ${noSeventhIntersection}`
    );

    if (seventhSpacingOrInversion && noSeventhIntersection) {
      options.skills = options.skills.concat(seventhSkills);
    }

    this.debug(options.skills);
    if (!options.chords) options.chords = {};
    const chordType =
      options.chords.type ||
      (!utils.intersects(seventhSkills, options.skills)
        ? 'triads'
        : utils.intersects(triadSkills, options.skills)
        ? 'both'
        : 'sevenths');
    options.chords.type = chordType;

    options.chords.spacing =
      options.chords.spacing ||
      (utils.intersects(spacingSkills, options.skills) ? 'open' : 'close');

    options.quality =
      typeof options.quality !== 'undefined'
        ? options.quality
        : !!utils.intersects(triadSkills.concat(seventhSkills), options.skills);
    // this.debug('options.quality', options.quality);

    // If we're doing quality, be sure we have some qualities:
    if (options.quality) {
      const qualitySkills = utils
        .intersection(triadSkills, options.skills)
        .concat(utils.intersection(seventhSkills, options.skills));
      if (qualitySkills.length < 2) {
        options.skills = options.skills.concat(
          options.chords.type === 'both'
            ? triadSkills.concat(seventhSkills)
            : options.chords.type === 'triads'
            ? triadSkills
            : options.chords.type === 'sevenths'
            ? seventhSkills
            : []
        );
      }
    }

    const figuresIntersection = utils.intersects(
      ['triad_figured_bass', 'seventh_chord_figured_bass'],
      options.skills
    );
    const numbersIntersection = utils.intersects(
      ['triad_inversion', 'seventh_chord_inversion'],
      options.skills
    );

    if (typeof options.inversion === 'undefined') {
      if (numbersIntersection && figuresIntersection) {
        options.inversion = utils.shuffle(['figures', 'numbers'])[0] as 'numbers' | 'figures';
      } else {
        options.inversion = figuresIntersection
          ? 'figures'
          : numbersIntersection
          ? 'numbers'
          : false;
      }
    }

    options.root = typeof options.root !== 'undefined' ? options.root : Math.random() > 0.5;

    options.grandStaff = options.chords.spacing === 'open';

    this.settings = {
      instructions: true,
      editor: {
        disableTextInput: true,
        fixedWidth: 150,
        ariaTitle: {
          keySignature: false,
          barline: false,
          clef: true,
          rhythm: false,
        },
      },
      letters: {
        chordIdButtons: {
          inversionType: options.inversion,
          // chordTypes: options.quality ? options.chords.type : false,
          chordTypes: chordType,
          root: options.root,
          quality: options.quality,
          checkButtonText: options.testMode ? 'Submit' : 'Check',
        },
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    return super.init(displayDiv, options);
  }

  initializeMusic(): void {
    const keySig = new KeySignature('C', 'Major');
    this.staves = this.options.grandStaff
      ? ['treble', 'bass'].map(makeStaff, this)
      : ['treble'].map(makeStaff, this);

    const musicElements = this.staves.reduce(
      (arr, staff) => arr.concat([keySig, staff.staff, staff.voice]),
      []
    );

    this.music = new Music(musicElements);

    function makeStaff(clefName): {
      staff: Staff;
      voice: Voice;
      noteHolder: NoteHolder;
      clef: Clef;
    } {
      const clef = new Clef(clefName);
      const staff = new Staff(clef);
      const nh = new NoteHolder('', 'w');
      nh.editable = new Editable(nh, {
        pitch: false,
        accidental: false,
        deleteable: ['ghost'],
        selectable: (this as ChordAndInversionID).options.root,
      });
      const measure = new Measure(nh);
      const voice = new Voice(measure);
      voice.setStaff(staff);
      return {
        staff,
        voice,
        noteHolder: nh,
        clef,
      };
    }
  }

  /*
   * generates questions
   * {
   *   chord: chord
   *   text: [questionNumber],
   *   skills: [skills]
   * }
   *
   */
  generateQuestions(questions): void {
    questions = questions || this.options.questions;
    this.questionNumber = 0;
    this.options.maxQuestions = this.options.drill
      ? this.options.maxQuestions || 15
      : questions.length;
    this.questionPrompt = this.prepareQuestionPrompt();

    this.qg = new QuestionGenerator();

    this.questions = this.options.drill
      ? this.generateDrillQuestions()
      : this.processManualQuestions(questions);
  }

  generateDrillQuestions(): ChordAndInversionIDQuestion[] {
    const skills = this.options.skills;
    const chordStrings = this.qg.getChords(skills, this.options.maxQuestions || 10);
    return chordStrings.map(this.processQuestion, this);
  }

  processManualQuestions(questions?: string[]): ChordAndInversionIDQuestion[] {
    questions = questions || this.options.questions;
    return questions.map(this.processQuestion, this);
  }

  processQuestion(chordString: string, index: number): ChordAndInversionIDQuestion {
    const clef = clefNames.find((potentialClef) => chordString.includes(potentialClef));
    if (clef) chordString = chordString.replace(`/${clef}`, '');

    let noteHolder: NoteHolder;
    if (chordString.includes('[')) {
      const re = /\[(.+)]/;
      const results = re.exec(chordString);
      if (!results[1]) throw new Error(`Invalid question string ${chordString}`);
      noteHolder = NoteHolder.fromString(results[1]);
      if (!noteHolder.rhythm) noteHolder.rhythm = new Rhythm('w');
    }

    const chord = noteHolder ? Chord.fromNoteHolder(noteHolder) : new Chord(chordString);
    if (!chord) {
      throw new Error(
        `Cannot process question, "${chordString}" is not a valid chord name or noteHolder string.`
      );
    }

    const skills = this.getChordSkills(chord);

    return {
      chord,
      text: index,
      skills,
      clef,
      noteHolder,
    };
  }

  getChordSkills(chord): string[] {
    let chordSkills = this.qg.getChordSkills(chord);
    if (this.options.chords.spacing === 'open') {
      chordSkills.push(chord.isTriad ? 'triad_spacing' : 'seventh_chord_spacing');
    }

    if (this.options.inversion) {
      chordSkills.push(
        ...this.qg.getChordInversionSkills(chord, this.options.inversion === 'figures')
      );
    }

    if (!this.options.quality) {
      // filter out quality if needed:
      chordSkills = chordSkills.filter((skill) => {
        skill = Skills.makeString(skill);
        if (skill === 'triad_inversion' || skill === 'seventh_chord_inversion') {
          return true;
        }
        return triadSkills.indexOf(skill) !== -1 && seventhSkills.indexOf(skill) !== -1;
      });
    }
    return chordSkills;
  }

  prepareQuestionPrompt(hideAnswerSpan = false): string {
    let prompt = '';
    if (this.options.root) {
      prompt += `${utils.isTouch() ? 'tap ' : 'click '}the root of the chord`;
    }
    let labelThe = '';
    if (this.options.inversion || this.options.quality) {
      labelThe = 'label the ';
      if (this.options.quality) labelThe += 'quality';
      if (this.options.quality && this.options.inversion) {
        labelThe += ' and ';
        if (prompt.length) prompt += ', ';
      } else if (prompt.length && (this.options.quality || this.options.inversion)) {
        prompt += ' and ';
      }

      if (this.options.inversion) {
        labelThe += this.options.inversion === 'figures' ? 'figured bass' : 'inversion';
      }
    }
    prompt += labelThe;
    prompt = `${prompt[0].toUpperCase() + prompt.slice(1)}.`;

    if (hideAnswerSpan) return prompt;

    prompt += "<br /><span id='answerSpan'>_</span>";

    return prompt;
  }

  addListeners(): void {
    // Code to set callbacks & communication to eventually check an answer.
    this.letterPanel.addCustomButtonCallback((value) => {
      if (value === 'check') this.checkAnswer();
      else this.updateAnswerDisplay();
    }, this);
    this.editor.addListener('noteSelect', this.updateRoot, this);
  }

  updateRoot(): void {
    const selected = this.editor.getSelected();
    if (selected.length) {
      const root = selected[0].editable.selectedNotes[0];
      this.letterPanel.chordIdButtons.updateRoot(root);
    }
  }

  updateAnswerDisplay(): void {
    const answerString = this.letterPanel.chordIdButtons.answerText as string;
    const answerSpan: HTMLSpanElement = document.getElementById('answerSpan');
    if (answerString === '__') {
      answerSpan.removeAttribute('role');
    } else {
      answerSpan.setAttribute('role', 'alert');
    }
    answerSpan.innerHTML = answerString;
    if (!this.answer.root) {
      this.editor.deselectAll();
    }
  }

  newQuestion(): void {
    super.newQuestion();
    this.letterPanel.chordIdButtons.reset();
    this.updateAnswerDisplay();
    this.editor.deselectAll();
    this.staves.forEach((staff) => {
      staff.noteHolder.length = 0;
    }, this);
    const chord = this.currentQuestion.chord;
    if (this.options.chords.spacing === 'open') {
      const nh = this.currentQuestion.noteHolder || chord.getOpenSpacing();
      nh.notes.forEach((note, index) => {
        const clefs = note.prefersClef();
        let staff;
        // if two possible clefs, assign it based on chorale spacing
        if (clefs.length > 1) {
          staff = index <= 1 ? BASS : TREBLE;
        } else {
          staff = clefs[0] === 'treble' ? TREBLE : BASS;
        }
        this.staves[staff].noteHolder.push(note.clone());
      });
    } else if (this.currentQuestion.noteHolder) {
      let { clef } = this.currentQuestion;
      const { noteHolder } = this.currentQuestion;
      clef = clef || noteHolder.prefersClef()[0];
      // this.debug('preferred clef:', clef);
      this.staves[0].clef.setName(clef);
      this.staves[0].noteHolder.notes = noteHolder.notes;
    } else {
      // Figure out what octaves have a single clef that's possible:
      let octaves = [];
      [2, 3, 4, 5, 6].forEach((octave) => {
        chord.setOctave(octave);
        const clefs = chord.noteHolder.prefersClef();
        clefs.forEach((clef) => octaves.push({ octave, clef }));
      });
      if (this.currentQuestion.clef) {
        // this.debug('octaves 1');
        // this.debug(octaves);
        octaves = octaves.filter((octave) => octave.clef === this.currentQuestion.clef);
      }
      // this.debug('octaves 2');
      // this.debug(octaves);

      // Randomly choose among these & set octave & clef:
      chord.setOctave(utils.shuffle(octaves)[0].octave);
      this.staves[0].clef.setName(octaves[0].clef);
      // Add the notes:
      this.staves[0].noteHolder.notes = chord.noteHolder.notes.map((note) => note.clone());
    }
    this.editor.redraw();
  }

  get answer(): ChordIdAnswer {
    return this.letterPanel.chordIdButtons.answer;
  }

  checkAnswer(): void {
    if (this.rightAnswerDelay) return;
    const givenChord = this.currentQuestion.chord;
    let seventhMismatch = false;
    const options = this.options;
    const answer = this.answer;
    let root;
    let quality;
    let inversion;
    let figures;
    // Check the root of the chord:
    if (options.root) {
      const nh = this.editor.getSelected()[0];
      root = nh ? nh.editable.selectedNotes[0] : false;
    } else {
      root = givenChord.root;
    }

    const clickTap = utils.isTouch() ? 'tap' : 'click on';
    if (!root) {
      this.checkMark.warn(`You must first ${clickTap} the root of the chord.`);
      return;
    }

    if (options.quality) {
      quality = answer.quality + (answer.isSeventh ? '7' : '');
      if (!quality) {
        this.checkMark.warn('You must first identify the quality of the chord.');
        return;
      }
    } else {
      quality = givenChord.quality;
    }

    if (options.inversion === 'numbers') {
      inversion = answer.inversion;
      if (inversion === '') {
        this.checkMark.warn('You must first provide an inversion.');
        return;
      }
    } else if (!options.inversion) {
      inversion = givenChord.inversion;
    }

    if (options.inversion === 'figures') {
      figures = answer.inversion;
      if (Chord.isValidFigures(figures)) {
        inversion = Chord.figuresToInversion(figures);
        seventhMismatch =
          Chord.figuresAreSeventh(figures) !== Chord.normalizeQuality(quality).isSeventh;
      }
      inversion = Chord.isValidFigures(answer.inversion)
        ? Chord.figuresToInversion(answer.inversion)
        : false;
    } else if (options.quality) {
      seventhMismatch = Chord.normalizeQuality(quality).isSeventh !== answer.isSeventh;
    }

    let studentAnswerChord: Chord;
    try {
      studentAnswerChord = new Chord(root, quality, inversion);
    } catch (err) {
      // this happens when there's not a valid chord. We swallow the error.
    }

    const correct = givenChord.equalsIgnoreOctave(studentAnswerChord);

    if (this.options.testMode) {
      this.checkpointAnswer(correct);
      return;
    }

    if (inversion === false || seventhMismatch) {
      this.wrongAnswer();
      return;
    }

    if (correct) {
      this.rightAnswer(1100);
    } else {
      this.wrongAnswer();
    }
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString): Promise<void> {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      return Promise.resolve(this.newQuestion());
    });
  }

  playAnswer(): void {
    this.player.playMusic(this.music, { tempo: 180 });
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(correct = 0): AnswerAndData {
    const chord = this.currentQuestion.chord;
    const correctAnswerText = chord.toString({
      root: 'short',
      inversion:
        this.options.inversion === 'numbers'
          ? 'abbreviation'
          : this.options.inversion
          ? 'figures'
          : false,
      accidentalFont: true,
    });

    const answerAndData: AnswerAndData = {
      question: chord.toString(),
      questionType: 'ChordAndInversionID',
      questionText: this.prepareQuestionPrompt(true),
      questionSVG: this.editor.exportSVG(),
      studentAnswerText: `${this.letterPanel.chordIdButtons.answerText}` || '_',
      studentAnswerSVG: null,
      correctAnswerText,
      rightAnswers: correct * 1,
      wrongAnswers: correct * -1 + 1,
      skills: this.currentQuestion.skills,
    };
    return answerAndData;
  }

  static registry = [
    {
      description: 'Identify triads (root position, close spacing)',
      skills: ['major_triads', 'minor_triads', 'augmented_triads', 'diminished_triads'],
      options: { root: false },
    },
    {
      description: 'Identify seventh chords (root position, close spacing)',
      skills: [
        'major_seventh_chords',
        'minor_seventh_chords',
        'dominant_seventh_chords',
        'half_diminished_seventh_chords',
        'diminished_seventh_chords',
      ],
      options: { root: false },
    },
    {
      description: 'Identify chords on the staff',
      skills: [
        'major_triads',
        'minor_triads',
        'augmented_triads',
        'diminished_triads',
        'triad_inversion',
        'triad_spacing',
        'seventh_chord_spacing',
        'triad_figured_bass',
        'major_seventh_chords',
        'minor_seventh_chords',
        'dominant_seventh_chords',
        'half_diminished_seventh_chords',
        'diminished_seventh_chords',
        'seventh_chord_inversion',
        'seventh_chord_figured_bass',
      ],
      alsoTrains: ['triad_spacing', 'seventh_chord_spacing'],
    },
  ];
} // end inherit
